import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Slide,
} from '@mui/material'
import { styled } from '@mui/material/styles'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />
})

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    maxWidth: '500px',
    margin: theme.spacing(2),
  },
}))

const AcceptButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#232536',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#ffcf51',
    color: '#232536',
  },
  textTransform: 'none',
  fontWeight: 500,
  minWidth: '140px',
}))

const PersonaliseButton = styled(Button)(({ theme }) => ({
  color: '#232536',
  borderColor: '#232536',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#232536',
  },
  textTransform: 'none',
  fontWeight: 500,
  minWidth: '140px',
}))

// Cookie management utilities
const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

const setCookie = (name, value, days = 365) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`
}

// Check if user is in EEA, UK, or Switzerland
// const isEEAUser = () => {
//   return true // Show to all users for safety
// }
// EEA countries, UK, and Switzerland country codes
const RESTRICTED_COUNTRIES = [
  // EEA countries
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IS',
  'IE',
  'IT',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  // UK and Switzerland
  'GB',
  'CH',
]

// Check if user is in EEA, UK, or Switzerland using multiple methods
const isEEAUser = async () => {
  try {
    // Method 1: Check cached result first
    const cachedResult = getCookie('geo-check')
    if (cachedResult) {
      return cachedResult === 'true'
    }

    // Method 2: Try timezone detection as primary method
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const europeanTimezones = [
      'Europe/',
      'Atlantic/Reykjavik',
      'Atlantic/Faroe',
      'Atlantic/Canary',
    ]

    const isEuropeanTimezone = europeanTimezones.some((tz) =>
      timezone.startsWith(tz)
    )

    if (isEuropeanTimezone) {
      // Cache the result for 24 hours
      setCookie('geo-check', 'true', 1)
      return true
    }

    // Method 3: Try IP geolocation API as fallback
    try {
      const response = await fetch('https://ipapi.co/country_code/', {
        method: 'GET',
        timeout: 3000, // 3 second timeout
      })

      if (response.ok) {
        const countryCode = await response.text()
        const isRestricted = RESTRICTED_COUNTRIES.includes(
          countryCode.trim().toUpperCase()
        )

        // Cache the result for 24 hours
        setCookie('geo-check', isRestricted.toString(), 1)
        return isRestricted
      }
    } catch (apiError) {
      // console.log('IP geolocation API failed, using timezone fallback')
    }

    // Method 4: Conservative fallback - if timezone suggests Europe but we can't confirm, show banner
    if (isEuropeanTimezone) {
      setCookie('geo-check', 'true', 1)
      return true
    }

    // Method 5: Final fallback - don't show banner if we can't determine location
    setCookie('geo-check', 'false', 1)
    return false
  } catch (error) {
    console.error('Error checking user location:', error)
    // Conservative approach: show banner if there's an error in detection
    setCookie('geo-check', 'true', 1)
    return true
  }
}
const CookieConsentBanner = ({ onConsentChange }) => {
  const [open, setOpen] = useState(false)
  // const [showPersonalise, setShowPersonalise] = useState(false)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    const checkUserLocation = async () => {
      try {
        const hasConsent = getCookie('cookie-consent')
        if (hasConsent) {
          // User already has consent set, parse it

          // if (needsConsent && !hasConsent) {
          //   setOpen(true)
          // } else if (hasConsent) {
          try {
            const consent = JSON.parse(hasConsent)
            onConsentChange(consent)
          } catch (e) {
            console.error('Error parsing consent cookie:', e)
          }
          setLoading(false)
          return
        }
        // Check if user needs consent
        const needsConsent = await isEEAUser()
        if (needsConsent) {
          setOpen(true)
        }
        setLoading(false)
      } catch (error) {
        console.error('Error in location check:', error)
        setLoading(false)
      }
    }
    checkUserLocation()
  }, [onConsentChange])

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      timestamp: new Date().toISOString(),
      version: '1.0',
    }

    setCookie('cookie-consent', JSON.stringify(consent))
    onConsentChange(consent)
    setOpen(false)
  }

  const handleAcceptEssential = () => {
    const consent = {
      essential: true,
      analytics: false,
      timestamp: new Date().toISOString(),
      version: '1.0',
    }

    setCookie('cookie-consent', JSON.stringify(consent))
    onConsentChange(consent)
    setOpen(false)
  }

  // const handlePersonalise = () => {
  //   setShowPersonalise(!showPersonalise)
  // }

  // Don't render anything while loading or if banner shouldn't show
  if (loading || !open) return null

  return (
    <StyledDialog
      open={open}
      TransitionComponent={Transition}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return
      }}
    >
      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='h6' gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Welcome!
        </Typography>

        <Typography
          variant='body2'
          sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}
        >
          We use cookies to enhance your experience on our website and to ensure
          it works properly. We collect data to help us understand how the
          website is being used, so we can work to make your experience on the
          site better. Read more information in our{' '}
          <Link
            href='/cookie-policy'
            sx={{ color: '#d32f2f', textDecoration: 'underline' }}
          >
            Cookie Policy
          </Link>
        </Typography>

        {/* {showPersonalise && (
          <Box
            sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}
          >
            <Typography variant='body2' sx={{ mb: 1, fontWeight: 500 }}>
              Cookie Settings:
            </Typography>
            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              • Essential cookies: Always enabled (required for site
              functionality)
            </Typography>
            <Typography
              variant='caption'
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              • Analytics cookies: Help us improve our website (optional)
            </Typography>
          </Box>
        )} */}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center', gap: 2 }}>
        <PersonaliseButton variant='outlined' onClick={handleAcceptEssential}>
          Accept Essential Only
        </PersonaliseButton>

        <AcceptButton variant='contained' onClick={handleAcceptAll}>
          Accept All Cookies
        </AcceptButton>
        {/* {!showPersonalise ? (
          <>
            <PersonaliseButton variant='outlined' onClick={handlePersonalise}>
              Personalise
            </PersonaliseButton>
            <AcceptButton variant='contained' onClick={handleAcceptAll}>
              Accept all cookies
            </AcceptButton>
          </>
        ) : (
          <>
            <PersonaliseButton
              variant='outlined'
              onClick={handleAcceptEssential}
            >
              Accept Essential Only
            </PersonaliseButton>
            <AcceptButton variant='contained' onClick={handleAcceptAll}>
              Accept All Cookies
            </AcceptButton>
          </>
        )} */}
      </DialogActions>
    </StyledDialog>
  )
}

export default CookieConsentBanner
